<script lang="ts">
  import { getContext } from 'svelte';
  import dayjs from 'dayjs';
  import { cx } from '@emotion/css';
  import { key, type SanityContext } from '$lib/context/sanity';
  import type { MediaKitAssetSection } from '$lib/sanity/schemas';
  import type { Carousel as CarouselSchema } from '$lib/sanity/schemas';
  import { ComponentType } from '$lib/sanity/types';
  import { ResponsiveVars, TwistBox } from '@twist/core';
  import { BGMode } from '$components/News/Background/BackgroundGradient.svelte';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';
  import Carousel from '$components/SanityComponents/Common/Carousel.svelte';
  import BlockButton from '$components/Button/BlockButton.svelte';
  import Container from '$components/News/Container.svelte';

  export let document: MediaKitAssetSection;
  export let sourceMap: ContentSourceMap | undefined;
  export let path: StudioPathLike = [];

  const { fileUrlBuilder } = getContext<SanityContext>(key);

  const carouselDocument: CarouselSchema = {
    _id: `${document.title}Carousel`,
    _type: ComponentType.Carousel,
    name: `${document.title}Carousel`,
    images: document.images,
    imageRefs: document.imageRefs,
    fullWidth: true
  };

  $: lastUpdated = dayjs(document.lastUpdated).format('MM.DD.YY');
  $: fileUrl = document.file && fileUrlBuilder.url(document.file.asset);
  $: encodeDataAttribute = useEncodeDataAttribute(document, sourceMap, sanityStudioURL);

  const responsiveVars = new ResponsiveVars()
    .addFluid('container-padding', [
      ['min', 64],
      ['lg', 'max', 64, 128],
      ['max', 128]
    ])
    .addFluid('last-updated-font-size', [
      ['min', 10],
      ['sm', 'lg', 10, 12],
      ['lg', 'max', 12, 18],
      ['max', 18]
    ])
    .addFluid('header-font-size', [
      ['min', 48],
      ['sm', 'lg', 48, 52],
      ['lg', 'max', 52, 72],
      ['max', 72]
    ])
    .addFluid('caption-font-size', [
      ['min', 14],
      ['sm', 'lg', 14, 16],
      ['lg', 'max', 16, 32],
      ['max', 32]
    ])
    .addFluid('caption-margin', [
      ['min', 24],
      ['lg', 'max', 24, 32],
      ['max', 32]
    ])
    .toCSS();
</script>

<div
  class={cx(responsiveVars, 'assetSectionContainer')}
  data-background
  data-bgmode={BGMode.SINGLE}
  data-bgcolor1={document.bgColor}
>
  <Container>
    <p class="lastUpdated">
      LAST UPDATED:
      <span class="date" data-sanity={encodeDataAttribute([...path, 'lastUpdated'])}>
        {lastUpdated}
      </span>
    </p>

    <h2 class="header" data-sanity={encodeDataAttribute([...path, 'title'])}>{document.title}</h2>

    <p class="caption" data-sanity={encodeDataAttribute([...path, 'caption'])}>
      {document.caption}
    </p>

    <TwistBox
      marginBottom={[
        ['min', 48],
        ['lg', 'max', 48, 64],
        ['max', 64]
      ]}
    >
      <BlockButton
        variant="fill"
        shape="download"
        motion="never"
        target="_blank"
        href={fileUrl}
        data-sanity={encodeDataAttribute([...path, 'file'])}
      >
        DOWNLOAD ALL
        <svg
          slot="icon"
          height="100%"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle class="svg-background" cx="18" cy="18" r="18" fill="black" />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M19 8H17V19.5858L13 15.5858L11.5858 17L17.2929 22.7071C17.6834 23.0976 18.3166 23.0976 18.7071 22.7071L24.4142 17L23 15.5858L19 19.5858V8Z"
            fill="white"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M11 25.5V27.5H25V25.5H11Z"
            fill="white"
          />
        </svg>
      </BlockButton>
    </TwistBox>
  </Container>

  <Carousel {sourceMap} {path} document={carouselDocument} />
</div>

<style lang="scss">
  .assetSectionContainer {
    padding: var(--container-padding) 0;
  }

  .lastUpdated {
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    font-size: var(--last-updated-font-size);
    font-weight: 400;
    letter-spacing: 1.2px;

    .date {
      font-weight: 700;
    }
  }

  .header {
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    font-size: var(--header-font-size);
    font-weight: 700;
  }

  .caption {
    color: var(--twist-color-gray-100);
    font-family: var(--font-family-primary);
    font-size: var(--caption-font-size);
    font-weight: 400;
    line-height: 150%;
    margin: var(--caption-margin) 0;
  }
</style>
